/* import macro from '@kitware/vtk.js/macro';

import vtkSliceRepresentationProxy from '@kitware/vtk.js/Proxy/Representations/SliceRepresentationProxy';
import ImagePropertyConstants from '@kitware/vtk.js/Rendering/Core/ImageProperty/Constants';
const { InterpolationType } = ImagePropertyConstants

const SLICE_MODE_MAP = {
  X: 'I',
  Y: 'J',
  Z: 'K',
  I: 'I',
  J: 'J',
  K: 'K',
};

function vtkIJKSliceRepresentationProxy(publicAPI, model) {
  model.classHierarchy.push('vtkIJKSliceRepresentationProxy');

  //model.property.setInterpolationType(InterpolationType.NEAREST);
  model.property.setInterpolationType(InterpolationType.LINEAR);

  const superClass = { ...publicAPI };

  // pretend XYZ slicing is actually just IJK.
  publicAPI.setSlicingMode = (modeString) => {
    return superClass.setSlicingMode(SLICE_MODE_MAP[modeString]);
  };
}

// ----------------------------------------------------------------------------
// Object factory
// ----------------------------------------------------------------------------

const DEFAULT_VALUES = {};

// ----------------------------------------------------------------------------

export function extend(publicAPI, model, initialValues = {}) {
  Object.assign(model, DEFAULT_VALUES, initialValues);

  // Object methods
  vtkSliceRepresentationProxy.extend(publicAPI, model);

  macro.proxyPropertyMapping(publicAPI, model, {
    opacity: { modelKey: 'property', property: 'opacity' },
  });

  // Object specific methods
  vtkIJKSliceRepresentationProxy(publicAPI, model);
}

// ----------------------------------------------------------------------------

export const newInstance = macro.newInstance(
  extend,
  'vtkIJKSliceRepresentationProxy'
);

// ----------------------------------------------------------------------------

export default { newInstance, extend };

 */

import macro from '@kitware/vtk.js/macro';
import vtkSliceRepresentationProxy from '@kitware/vtk.js/Proxy/Representations/SliceRepresentationProxy';
import { SlicingMode } from '@kitware/vtk.js/Rendering/Core/ImageMapper/Constants';
import ImagePropertyConstants from '@kitware/vtk.js/Rendering/Core/ImageProperty/Constants';

const { InterpolationType } = ImagePropertyConstants;

const SLICE_MODE_MAP = {
  X: 'I',
  Y: 'J',
  Z: 'K',
  I: 'I',
  J: 'J',
  K: 'K',
};

const SLICE_MODE_TO_DIMS = {
  [SlicingMode.I]: d => [Math.max(1, d[1]), Math.max(1, d[2])],
  [SlicingMode.J]: d => [Math.max(1, d[0]), Math.max(1, d[2])],
  [SlicingMode.K]: d => [Math.max(1, d[0]), Math.max(1, d[1])],
};

function vtkIJKSliceRepresentationProxy(publicAPI, model) {
  model.classHierarchy.push('vtkIJKSliceRepresentationProxy');

  // Never picked — background image slice.
  model.actor.setPickable(false);
  // Hide until data arrives so no 'No input!' errors fire during early renders.
  model.actor.setVisibility(false);
  model.mapper.onModified(() => {
    if (model.mapper.getInputData?.()) {
      model.actor.setVisibility(true);
    }
  });

  const superClass = { ...publicAPI };

  // Returns [w, h] for the active slice plane, or null if no image connected yet.
  function getSliceTextureDims() {
    const image = model.mapper?.getInputData?.();
    if (!image || typeof image.getDimensions !== 'function') return null;
    const d = image.getDimensions();
    const slicingMode = model.mapper.getSlicingMode?.();
    const dimsFn
      = SLICE_MODE_TO_DIMS[slicingMode] ?? SLICE_MODE_TO_DIMS[SlicingMode.K];
    return dimsFn(d);
  }

  function getNumComponents() {
    const image = model.mapper?.getInputData?.();
    return (
      image?.getPointData?.()?.getScalars?.()?.getNumberOfComponents?.() ?? 1
    );
  }

  function getOpenGLViewSpec() {
    const mapperSpec = model.mapper.getViewSpecificProperties?.() ?? {};
    const openGLSpec = mapperSpec.OpenGL ?? {};
    return { mapperSpec, openGLSpec };
  }

  function setShaderReplacements(shaderReplacements) {
    const { mapperSpec, openGLSpec } = getOpenGLViewSpec();
    model.mapper.setViewSpecificProperties({
      ...mapperSpec,
      OpenGL: {
        ...openGLSpec,
        ShaderReplacements: shaderReplacements,
      },
    });
  }

  function clearCubicShaderReplacements() {
    const { mapperSpec, openGLSpec } = getOpenGLViewSpec();

    const { ShaderReplacements: _ignored, ...restOpenGL } = openGLSpec;
    model.mapper.setViewSpecificProperties({
      ...mapperSpec,
      OpenGL: restOpenGL,
    });
    model._cubicTexW = undefined;
    model._cubicTexH = undefined;
    model._cubicNumComp = undefined;
  }

  // Returns true if shader replacements were (re-)injected, false if skipped.
  function applyCubicShaderReplacements() {
    const dims = getSliceTextureDims();
    if (!dims) {
      model._cubicTexW = undefined;
      model._cubicTexH = undefined;
      model._cubicNumComp = undefined;
      return false;
    }

    // Independent-components (multi-LUT) path is too complex to replicate;
    // fall back to VTK's built-in shader for that case.
    const iComps
      = model.actor.getProperty?.()?.getIndependentComponents?.() ?? false;
    if (iComps) {
      throw new Error(
        'independent-components image; cubic shader not supported',
      );
    }

    const numComp = getNumComponents();
    const [w, h] = dims;

    // Nothing changed — existing shader still valid.
    if (
      model._cubicTexW === w
      && model._cubicTexH === h
      && model._cubicNumComp === numComp
    ) {
      return false;
    }

    model._cubicTexW = w;
    model._cubicTexH = h;
    model._cubicNumComp = numComp;

    const invW = 1 / w;
    const invH = 1 / h;

    // TCoord::Impl body varies by component count to mirror VTK's built-in paths.
    let tcoordImpl;
    if (numComp === 1) {
      tcoordImpl = `
vec4 tvalue = vtkSampleBicubic(texture1, tcoordVCVSOutput);
float intensity = tvalue.r;
vec3 tcolor = texture2D(colorTexture1, vec2(intensity * cscale0 + cshift0, 0.5)).rgb;
float scalarOpacity = texture2D(pwfTexture1, vec2(intensity * pwfscale0 + pwfshift0, 0.5)).r;
gl_FragData[0] = vec4(tcolor, scalarOpacity * opacity);
`;
    }
    else if (numComp === 2) {
      tcoordImpl = `
vec4 tvalue = vtkSampleBicubic(texture1, tcoordVCVSOutput);
float intensity = tvalue.r * cscale0 + cshift0;
gl_FragData[0] = vec4(texture2D(colorTexture1, vec2(intensity, 0.5)).rgb,
                      pwfscale0 * tvalue.g + pwfshift0);
`;
    }
    else if (numComp === 3) {
      tcoordImpl = `
vec4 tvalue = cscale0 * vtkSampleBicubic(texture1, tcoordVCVSOutput.st) + cshift0;
gl_FragData[0] = vec4(
  texture2D(colorTexture1, vec2(tvalue.r, 0.5)).r,
  texture2D(colorTexture1, vec2(tvalue.g, 0.5)).r,
  texture2D(colorTexture1, vec2(tvalue.b, 0.5)).r,
  opacity);
`;
    }
    else {
      // 4+ components
      tcoordImpl = `
vec4 tvalue = cscale0 * vtkSampleBicubic(texture1, tcoordVCVSOutput.st) + cshift0;
gl_FragData[0] = vec4(
  texture2D(colorTexture1, vec2(tvalue.r, 0.5)).r,
  texture2D(colorTexture1, vec2(tvalue.g, 0.5)).r,
  texture2D(colorTexture1, vec2(tvalue.b, 0.5)).r,
  tvalue.a);
`;
    }

    const shaderReplacements = [
      {
        shaderType: 'Fragment',
        originalValue: '//VTK::TCoord::Dec',
        replacementValue: `
varying vec2 tcoordVCVSOutput;
uniform float cshift0;
uniform float cscale0;
uniform float pwfshift0;
uniform float pwfscale0;
uniform sampler2D texture1;
uniform sampler2D colorTexture1;
uniform sampler2D pwfTexture1;
uniform float opacity;

const vec2 vtkInvTexSize = vec2(${invW}, ${invH});

vec4 vtkCubicHermite(vec4 A, vec4 B, vec4 C, vec4 D, float t) {
  vec4 a = -0.5 * A + 1.5 * B - 1.5 * C + 0.5 * D;
  vec4 b = A - 2.5 * B + 2.0 * C - 0.5 * D;
  vec4 c = -0.5 * A + 0.5 * C;
  vec4 d = B;
  return ((a * t + b) * t + c) * t + d;
}

vec4 vtkSampleBicubic(sampler2D tex, vec2 uv) {
  vec2 xy = uv / vtkInvTexSize - 0.5;
  vec2 f = fract(xy);
  vec2 base = floor(xy) + 0.5;

  vec2 uv00 = (base + vec2(-1.0, -1.0)) * vtkInvTexSize;
  vec2 uv10 = (base + vec2( 0.0, -1.0)) * vtkInvTexSize;
  vec2 uv20 = (base + vec2( 1.0, -1.0)) * vtkInvTexSize;
  vec2 uv30 = (base + vec2( 2.0, -1.0)) * vtkInvTexSize;

  vec2 uv01 = (base + vec2(-1.0,  0.0)) * vtkInvTexSize;
  vec2 uv11 = (base + vec2( 0.0,  0.0)) * vtkInvTexSize;
  vec2 uv21 = (base + vec2( 1.0,  0.0)) * vtkInvTexSize;
  vec2 uv31 = (base + vec2( 2.0,  0.0)) * vtkInvTexSize;

  vec2 uv02 = (base + vec2(-1.0,  1.0)) * vtkInvTexSize;
  vec2 uv12 = (base + vec2( 0.0,  1.0)) * vtkInvTexSize;
  vec2 uv22 = (base + vec2( 1.0,  1.0)) * vtkInvTexSize;
  vec2 uv32 = (base + vec2( 2.0,  1.0)) * vtkInvTexSize;

  vec2 uv03 = (base + vec2(-1.0,  2.0)) * vtkInvTexSize;
  vec2 uv13 = (base + vec2( 0.0,  2.0)) * vtkInvTexSize;
  vec2 uv23 = (base + vec2( 1.0,  2.0)) * vtkInvTexSize;
  vec2 uv33 = (base + vec2( 2.0,  2.0)) * vtkInvTexSize;

  vec4 r0 = vtkCubicHermite(texture2D(tex, uv00), texture2D(tex, uv10), texture2D(tex, uv20), texture2D(tex, uv30), f.x);
  vec4 r1 = vtkCubicHermite(texture2D(tex, uv01), texture2D(tex, uv11), texture2D(tex, uv21), texture2D(tex, uv31), f.x);
  vec4 r2 = vtkCubicHermite(texture2D(tex, uv02), texture2D(tex, uv12), texture2D(tex, uv22), texture2D(tex, uv32), f.x);
  vec4 r3 = vtkCubicHermite(texture2D(tex, uv03), texture2D(tex, uv13), texture2D(tex, uv23), texture2D(tex, uv33), f.x);

  return vtkCubicHermite(r0, r1, r2, r3, f.y);
}
`,
        replaceFirst: true,
        replaceAll: false,
      },
      {
        shaderType: 'Fragment',
        originalValue: '//VTK::TCoord::Impl',
        replacementValue: tcoordImpl,
        replaceFirst: true,
        replaceAll: false,
      },
    ];

    setShaderReplacements(shaderReplacements);
    return true;
  }

  function applyInterpolationMode(mode) {
    model.imageInterpolationMode = mode;

    if (mode === 'nearest') {
      clearCubicShaderReplacements();
      model.property.setInterpolationType(InterpolationType.NEAREST);
      publicAPI.modified();
      return;
    }

    if (mode === 'linear') {
      clearCubicShaderReplacements();
      model.property.setInterpolationType(InterpolationType.LINEAR);
      publicAPI.modified();
      return;
    }

    if (mode === 'cubic') {
      model.property.setInterpolationType(InterpolationType.NEAREST);
      try {
        if (applyCubicShaderReplacements()) {
          publicAPI.modified();
        }
      }
      catch (e) {
        console.warn(
          '[IJKSliceRepProxy] Cubic interpolation failed, falling back to linear.',
          e,
        );
        applyInterpolationMode('linear');
      }
      return;
    }

    throw new Error(
      `[vtkIJKSliceRepresentationProxy] Unknown interpolation mode: ${String(
        mode,
      )}`,
    );
  }

  function refreshCubicIfActive() {
    if (model.imageInterpolationMode !== 'cubic') return;
    try {
      if (applyCubicShaderReplacements()) {
        publicAPI.modified();
      }
    }
    catch (e) {
      console.warn(
        '[IJKSliceRepProxy] Cubic shader refresh failed, falling back to linear.',
        e,
      );
      applyInterpolationMode('linear');
    }
  }

  publicAPI.setImageInterpolationMode = (mode) => {
    applyInterpolationMode(mode);
  };

  publicAPI.getImageInterpolationMode = () => model.imageInterpolationMode;

  publicAPI.setSlicingMode = (modeString) => {
    const mapped = SLICE_MODE_MAP[modeString];
    if (mapped === undefined) {
      throw new Error(
        `[vtkIJKSliceRepresentationProxy] Unknown slicing mode: ${String(
          modeString,
        )}`,
      );
    }
    const ok = superClass.setSlicingMode(mapped);
    refreshCubicIfActive();
    return ok;
  };

  const superSetInputData = publicAPI.setInputData?.bind(publicAPI);
  if (superSetInputData) {
    publicAPI.setInputData = (...args) => {
      const out = superSetInputData(...args);
      refreshCubicIfActive();
      return out;
    };
  }

  const superSetInputConnection = publicAPI.setInputConnection?.bind(publicAPI);
  if (superSetInputConnection) {
    publicAPI.setInputConnection = (...args) => {
      const out = superSetInputConnection(...args);
      refreshCubicIfActive();
      return out;
    };
  }
}

const DEFAULT_VALUES = {
  imageInterpolationMode: 'linear',
};

export function extend(publicAPI, model, initialValues = {}) {
  Object.assign(model, DEFAULT_VALUES, initialValues);

  vtkSliceRepresentationProxy.extend(publicAPI, model);

  macro.proxyPropertyMapping(publicAPI, model, {
    opacity: { modelKey: 'property', property: 'opacity' },
  });

  vtkIJKSliceRepresentationProxy(publicAPI, model);

  // Safe to call here: default is 'linear' which does not touch shader
  // replacements, so no input-not-ready risk.
  publicAPI.setImageInterpolationMode(model.imageInterpolationMode);
}

export const newInstance = macro.newInstance(
  extend,
  'vtkIJKSliceRepresentationProxy',
);

export default { newInstance, extend };
